import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Typography,
  Box,
  Container,
  Modal,
  MenuItem,
  Button,
} from "@mui/material";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/request/RequestStatus";
import ActionButton from "../../shared/ActionButton";
import NoticeBanner from "../../shared/NoticeBanner";
import SearchContainer from "../shared/search/SearchContainer";
import SearchTabs from "../shared/search/filter/SearchTabs";
import MenuModal from "../../shared/MenuModal";
import Positions from "../shared/search/filter/Positions";
import Price from "../shared/search/filter/Price";
import Prefectures from "../shared/search/filter/Prefecture";
import ModalSp from "../../shared/ModalForSp";
import FilterTag from "../shared/search/FilterTag";
import useActiveFilterTags from "../../shared/hooks/useActiveFilterTags";
import { useFilterModal } from "../../shared/hooks/useFilterModal";
import {
  FilterModalHeader,
  FilterModalFooter,
} from "../../shared/FilterModalParts";
import useRemoveFilterTagAndSearch from "../../shared/hooks/useRemoveFilterTagAndSearch";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import { useSearchableIndex } from "../../../controllers/useSearchableIndex";
import { useUnread } from "../../shared/unread/UnreadProvider";
import { buildPrefByArea } from "../../utils/prefectureArea";
import { getMasterData } from "../../utils/masterDataClient";
import usePriceFilterInSearchFilters, {
  createInitialPriceFilter,
} from "../../shared/hooks/usePriceFilterInSearchFilters";
import { updateReadFlagById } from "../../utils/updateReadFlagById";
import {
  DEFAULT_PER_PAGE,
  createInitialPagination,
} from "../../utils/pagination";
import {
  PROJECT_COLOR,
  PROJECT_COLOR_LIGHT,
  PROJECT_COLOR_DARK,
  GRAY_TEXT_COLOR,
  TEXT_COLOR_PRIMARY,
  BORDER_COLOR_LIGHT,
} from "../../../constants/colors";
import useRequestStatus from "../shared/hooks/useRequestStatus";

const shouldShowProjectSearch = (pathname) => {
  // プロジェクト一覧画面でのみ表示
  return pathname === "/projects" || pathname === "/projects/";
};

const createInitialProjectSearchFilters = () => ({
  keyword: [],
  keywordInputText: "",
  roleIds: [],
  prefectureIds: [],
  priceFilter: createInitialPriceFilter(),
});

const initialPriceFilter = createInitialPriceFilter();

export default function ProjectLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const showSearchContainer = shouldShowProjectSearch(location.pathname);

  // レスポンシブ
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 共通hook
  const {
    exists: contractExists,
    isActive,
    isStatusLoading,
    modalOpen,
    handleChangeStatus,
    handleConfirmStop,
    handleCancelStop,
  } = useRequestStatus({
    fetchPath: "/user_contract_request/edit.json",
    togglePath: "/user_contract_request/toggle_recruiting",
    existsKey: "contract_request_exists",
    eventName: "contract:status-updated",
    requestParamKey: "user_contract_request",
    errorMessage: "読み込みに失敗しました。再読み込みしてください。",
  });

  const { markProjectsIndexSeen } = useUnread();

  // 表示するプロジェクト一覧
  const [projects, setProjects] = useState([]);

  // 一覧取得中フラグ
  const [loading, setLoading] = useState(true);

  // 現在ページ
  const [page, setPage] = useState(1);

  // 通常一覧のページング情報
  const [pagination, setPagination] = useState(
    createInitialPagination(DEFAULT_PER_PAGE),
  );

  const projectsAbortControllerRef = useRef(null);
  const projectsRequestIdRef = useRef(0);

  // スマホ用絞り込みメニューモーダル
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // 絞り込みモーダル
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // 選択中の絞り込み条件
  const [activeFilter, setActiveFilter] = useState(null);

  // ポジション
  const [roles, setRoles] = useState([]);

  // 勤務地
  const [prefectures, setPrefectures] = useState([]);

  // 検索結果上部へスクロール
  const scrollToResultsTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  // projects.jsonを取得してprojectsを更新
  const fetchProjects = useCallback(async (nextPage = 1) => {
    projectsAbortControllerRef.current?.abort();

    const controller = new AbortController();
    projectsAbortControllerRef.current = controller;
    const requestId = ++projectsRequestIdRef.current;

    setLoading(true);
    try {
      const res = await fetch(
        `/projects.json?page=${nextPage}&per=${DEFAULT_PER_PAGE}`,
        {
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
        },
      );

      if (!res.ok) {
        if (requestId !== projectsRequestIdRef.current) return;

        setProjects([]);
        setPagination(createInitialPagination(DEFAULT_PER_PAGE));
        return;
      }

      const json = await res.json();

      if (requestId !== projectsRequestIdRef.current) return;

      setProjects(json.projects);
      setPagination(
        json.pagination ?? createInitialPagination(DEFAULT_PER_PAGE),
      );
      setPage(json.pagination?.current_page ?? nextPage);
    } catch (error) {
      if (error.name === "AbortError") return;
      if (requestId !== projectsRequestIdRef.current) return;

      setProjects([]);
      setPagination(createInitialPagination(DEFAULT_PER_PAGE));
    } finally {
      if (requestId === projectsRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const {
    searchFilters,
    setSearchFilters,
    hasSearchFilters,
    hasExecutedSearch,
    markSearchAsNotExecuted,
    executeSearch,
    searchResults,
    mutateSearchResults,
    searchPagination,
    isSearching,
  } = useSearchableIndex({
    searchEndpoint: "/api/projects/search",
    fetchIndex: fetchProjects,
    createInitialSearchFilters: createInitialProjectSearchFilters,
    getResponseItems: (json) => json.projects,
  });

  const [draftSearchFilters, setDraftSearchFilters] = useState(
    createInitialProjectSearchFilters(),
  );

  const setDraftPriceFilter = useCallback((nextPriceFilterOrUpdater) => {
    setDraftSearchFilters((previousDraftSearchFilters) => {
      const previousPriceFilter = previousDraftSearchFilters.priceFilter ?? createInitialPriceFilter();

      const nextPriceFilter =
        typeof nextPriceFilterOrUpdater === "function"
          ? nextPriceFilterOrUpdater(previousPriceFilter)
          : nextPriceFilterOrUpdater;

      return {
        ...previousDraftSearchFilters,
        priceFilter: nextPriceFilter,
      };
    });
  }, []);

  const syncDraftSearchFilters = useCallback(() => {
    setDraftSearchFilters({
      ...searchFilters,
      keyword: [...(searchFilters.keyword ?? [])],
      roleIds: [...(searchFilters.roleIds ?? [])],
      prefectureIds: [...(searchFilters.prefectureIds ?? [])],
      priceFilter: {
        ...(searchFilters.priceFilter ?? createInitialPriceFilter()),
      },
    });
  }, [searchFilters]);

  const { setPriceFilterInSearchFilters } = usePriceFilterInSearchFilters({
    setSearchFilters,
    fallbackPriceFilter: initialPriceFilter,
  });

  const { handleRemoveFilterTagAndSearch } = useRemoveFilterTagAndSearch({
    setSearchFilters,
    executeSearch,
    prefectures,
  });

  // ポジションと都道府県を一覧で取得
  useEffect(() => {
    getMasterData()
      .then((masterData) => {
        setRoles(masterData.roles);
        setPrefectures(masterData.prefectures);
      })
      .catch((error) => {
        console.error("/api/master fetch failed", error);
        toast.error("情報の読み込みに失敗しました。再読み込みしてください。");
        setRoles([]);
        setPrefectures([]);
      });
  }, []);

  // 再レンダリングのたびに分類処理を走らせないため useMemo でキャッシュ
  const prefByArea = useMemo(() => buildPrefByArea(prefectures), [prefectures]);

  // スマホ：　選択されたポジションを表示(未選択の場合は「ポジション」を表示)
  const selectedRoleLabel = useMemo(() => {
    if (!searchFilters.roleIds?.length) return "";

    const selectedRoleIdSet = new Set(searchFilters.roleIds.map(String));

    const selectedRoleNames = roles
      .filter((role) => selectedRoleIdSet.has(String(role.id)))
      .map((role) => role.name);

    return selectedRoleNames.join("、");
  }, [searchFilters.roleIds, roles]);

  // スマホ：　指定された金額を表示(金額: ◯万円、年収: ◯万円〜◯万円, 未選択の場合は「金額/年収」を表示)
  const selectedPriceLabel = useMemo(() => {
    const priceValue = String(searchFilters.priceFilter?.value ?? "").trim();
    if (priceValue === "") return "";
    return `${priceValue}万円`;
  }, [searchFilters.priceFilter]);

  // スマホ: 選択された勤務地を表示(未選択の場合は「勤務地」を表示)
  const selectedPrefectureLabel = useMemo(() => {
    if (!searchFilters.prefectureIds?.length) return "";

    const selectedPrefectureIdSet = new Set(
      searchFilters.prefectureIds.map(String),
    );

    const selectedPrefectureNames = prefectures
      .filter((prefecture) =>
        selectedPrefectureIdSet.has(String(prefecture.id)),
      )
      .map((prefecture) => prefecture.name);

    return selectedPrefectureNames.join("、");
  }, [searchFilters.prefectureIds, prefectures]);

  // PC: 選択・指定した条件をタグにする
  const { activeFilterTags } = useActiveFilterTags({
    searchFilters,
    roles,
    prefectures,
  });

  // スマホならメニューモーダル、PCなら絞り込みモーダルを表示
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const {
    handleOpen,
    handleClose,
    modalStyle,
    deleteFilter,
    openFilterModal,
    modalTitle,
    modalHeaderSx,
    modalFooterOkButtonSx,
  } = useFilterModal({
    isMobile,
    setMenuAnchorEl,
    setFilterDialogOpen,
    setFilterModalOpen,
    setActiveFilter,
    activeFilter,
    executeSearch,
    searchFilters,
    setSearchFilters,
    selectedColor: TEXT_COLOR_PRIMARY,
    projectColor: PROJECT_COLOR,
    projectColorDark: PROJECT_COLOR_DARK,
    filterTitleMap: { price: "金額" },
    scrollToResultsTop,
  });

  // スマホ用絞り込み検索メニューモーダル
  const handleOpenMobileFilterMenu = useCallback(
    (event) => {
      syncDraftSearchFilters();
      handleOpen(event);
    },
    [syncDraftSearchFilters, handleOpen],
  );

  // PC用絞り込みモーダル
  const handleOpenDesktopFilterModal = useCallback(() => {
    syncDraftSearchFilters();
    handleOpen();
  }, [syncDraftSearchFilters, handleOpen]);

  const handleSubmitFilterModal = useCallback(() => {
    const nextSearchFilters = {
      ...searchFilters,
      roleIds: draftSearchFilters.roleIds,
      prefectureIds: draftSearchFilters.prefectureIds,
      priceFilter: draftSearchFilters.priceFilter,
    };
  
    setSearchFilters(nextSearchFilters);
    scrollToResultsTop();
    executeSearch(nextSearchFilters, {
      page: 1,
      per: DEFAULT_PER_PAGE,
    });
    handleClose();
  }, [
    searchFilters,
    draftSearchFilters,
    setSearchFilters,
    scrollToResultsTop,
    executeSearch,
    handleClose,
  ]);

  const handleCancelFilterModal = useCallback(() => {
    syncDraftSearchFilters();
    handleClose();
  }, [syncDraftSearchFilters, handleClose]);

  const modalFooter = (
    <FilterModalFooter
      onCancel={handleCancelFilterModal}
      onSubmit={handleSubmitFilterModal}
      leftLabel="キャンセル"
      rightLabel="OK"
      ActionButton={ActionButton}
      cancelButtonSx={{ bgcolor: "#D9D9D9" }}
      submitButtonSx={modalFooterOkButtonSx}
    />
  );

  const shouldShowSearchResults =
    hasSearchFilters && (hasExecutedSearch || isSearching);

  // 表示する一覧は「検索中は searchResults、それ以外は projects」
  const visibleProjects = shouldShowSearchResults ? searchResults : projects;

  // loading も検索中は isSearching を使う
  const visibleLoading = shouldShowSearchResults ? isSearching : loading;

  useEffect(() => {
    if (location.pathname === "/projects" || location.pathname === "/projects/") {
      markProjectsIndexSeen();
    }
  }, [location.pathname, markProjectsIndexSeen]);

  // pagination も検索中は検索用を使う
  const visiblePagination = shouldShowSearchResults
    ? searchPagination
    : pagination;

  useEffect(() => {
    return () => {
      projectsAbortControllerRef.current?.abort();
    };
  }, []);

  // 既読
  const handleMarkedRead = useCallback(
    (projectId) => {
      const updateProjects = (previousProjects) =>
        updateReadFlagById(previousProjects, projectId);
  
      setProjects(updateProjects);
      mutateSearchResults(updateProjects);
    },
    [mutateSearchResults],
  );

  // 遷移先
  const handleClick = () => {
    navigate(
      `/user_contract_request/edit?redirect=${window.location.pathname}`,
    );
  };

  // ページ移動
  const goToPage = useCallback(
    (nextPage) => {
      // 検索結果表示中なら、同じ条件でそのページを再検索
      if (shouldShowSearchResults) {
        scrollToResultsTop();
        executeSearch(searchFilters, { page: nextPage, per: DEFAULT_PER_PAGE });
        return;
      }

      // 通常一覧表示中の遷移
      setPage(nextPage);
      scrollToResultsTop();
      fetchProjects(nextPage);
    },
    [shouldShowSearchResults, executeSearch, searchFilters, fetchProjects, scrollToResultsTop],
  );

  // データ未取得時のローディング表示
  if (isStatusLoading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ===== 固定エリア ===== */}
      <Box
        sx={{
          position: "sticky",
          top: isDesktop ? "63px" : "45px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0.5,
          mx: -2,
          px: isDesktop ? 0 : 2,
          pt: isDesktop ? 1.5 : 1,
        }}
      >
        {/* --- 上段：タイトル / RequestStatus --- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              alignItems: isDesktop ? "center" : "stretch",
              gap: isDesktop ? 1.5 : 1,
            }}
          >
            {/* タイトル */}
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: isDesktop ? "1.8rem" : "1.2rem",
                whiteSpace: "nowrap",
              }}
            >
              プロジェクト
            </Typography>

            {/* タイトル寄りの RequestStatus */}
            {contractExists && (
              <RequestStatus
                viewerType="user"
                type="project"
                isActive={isActive}
                onToggle={handleChangeStatus}
              />
            )}

            {/* NoticeBanner + ActionButton */}
            <Box
              sx={{
                ml: isDesktop ? "auto" : 0,
                display: "flex",
                flexDirection: isDesktop ? "row" : "column-reverse",
                gap: 1,
                width: isDesktop ? "auto" : "100%",
                alignItems: "center",
              }}
            >
              <NoticeBanner
                text={
                  contractExists
                    ? "希望条件に変更があれば、更新しておきましょう。"
                    : "希望条件を入力すると、詳細を全て閲覧できます。"
                }
              />

              <ActionButton
                onClick={handleClick}
                label={`希望条件を${contractExists ? "編集" : "入力"}`}
                icon={<ModeEditOutlinedIcon sx={{ color: PROJECT_COLOR }} />}
                showBadge={!contractExists}
                sx={{
                  bgcolor: PROJECT_COLOR,
                  width: isDesktop ? "auto" : "100%",
                  "&:hover": { bgcolor: PROJECT_COLOR_DARK },
                }}
                badgeSx={{
                  bgcolor: PROJECT_COLOR_LIGHT,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* プロジェクト一覧画面のみ表示 */}
        {showSearchContainer && (
          <Box sx={{ mt: 1 }}>
            <SearchContainer
              type="project"
              value={searchFilters}
              onChange={(nextFilters) => {
                const prevKeywordCount = searchFilters.keyword?.length ?? 0;
                const nextKeywordCount = nextFilters.keyword?.length ?? 0;

                if (nextKeywordCount < prevKeywordCount) {
                  scrollToResultsTop();
                }

                setSearchFilters(nextFilters);

                if (!isSearching) {
                  markSearchAsNotExecuted();
                }
              }}
              onSearch={(filters) => {
                scrollToResultsTop();
                executeSearch(filters, { page: 1, per: DEFAULT_PER_PAGE });
              }}
              onOpenFilter={isMobile ? handleOpenMobileFilterMenu : handleOpenDesktopFilterModal}
            />
            {isDesktop && activeFilterTags.length > 0 && (
              <Box>
                <FilterTag
                  type="project"
                  selectedKeywords={activeFilterTags}
                  onDeleteTag={(keywordIndex) => {
                    const tagToRemove = activeFilterTags[keywordIndex];
                    if (!tagToRemove) return;
                    scrollToResultsTop();
                    handleRemoveFilterTagAndSearch(tagToRemove);
                  }}
                />
                <Button
                  variant="text"
                  onClick={() => {
                    const nextFilters = {
                      ...searchFilters,
                      roleIds: [],
                      prefectureIds: [],
                      priceFilter: createInitialPriceFilter(),
                    };

                    scrollToResultsTop();
                    setSearchFilters(nextFilters);
                    executeSearch(nextFilters, {
                      page: 1,
                      per: DEFAULT_PER_PAGE,
                    });
                  }}
                  sx={{ color: GRAY_TEXT_COLOR, mt: 1 }}
                >
                  フィルターをリセット
                </Button>
              </Box>
            )}
            {isMobile ? (
              <>
                <MenuModal
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={() => setMenuAnchorEl(null)}
                  paperSx={{ width: "100%", borderRadius: 1 }}
                >
                  <>
                    <MenuItem
                      onClick={() => {
                        syncDraftSearchFilters();
                        openFilterModal("position"); // ModalForSp を Positionで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              searchFilters.roleIds.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {searchFilters.roleIds.length > 0
                            ? selectedRoleLabel
                            : "ポジション"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        syncDraftSearchFilters();
                        openFilterModal("price"); // ModalForSp を Priceで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              selectedPriceLabel.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {selectedPriceLabel !== ""
                            ? selectedPriceLabel
                            : "金額"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        syncDraftSearchFilters();
                        openFilterModal("prefecture"); // ModalForSp を Prefectureで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              searchFilters.prefectureIds.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {searchFilters.prefectureIds.length > 0
                            ? selectedPrefectureLabel
                            : "勤務地"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        deleteFilter();
                        // NOTE: Mobile UX spec:
                        // Do NOT call setMenuAnchorEl(null) intentionally.
                        // Keep MenuModal open so users can see the currently applied conditions after closing FilterModal.
                        // Keep MenuModal open to immediately reflect the cleared conditions in this panel.
                      }}
                      sx={{ color: GRAY_TEXT_COLOR }}
                    >
                      フィルターをリセット
                    </MenuItem>
                  </>
                </MenuModal>
                <ModalSp
                  open={filterModalOpen}
                  onClose={handleCancelFilterModal}
                  header={({ modalTitleId }) => (
                    <FilterModalHeader
                      title={modalTitle}
                      headerSx={modalHeaderSx}
                      titleId={modalTitleId}
                    />
                  )}
                  footer={modalFooter}
                >
                  {activeFilter === "position" && (
                    <Positions
                      roles={roles}
                      selectedRoleIds={draftSearchFilters.roleIds}
                      onChangeSelectedRoleIds={(nextRoleIds) => {
                        setDraftSearchFilters((previousDraftSearchFilters) => ({
                          ...previousDraftSearchFilters,
                          roleIds: nextRoleIds,
                        }));
                      }}
                    />
                  )}

                  {activeFilter === "price" && (
                    <Price
                      priceFilter={draftSearchFilters.priceFilter}
                      setPriceFilter={setDraftPriceFilter}
                      label="希望金額"
                    />
                  )}

                  {activeFilter === "prefecture" && (
                    <Prefectures
                      prefByArea={prefByArea}
                      selectedIds={draftSearchFilters.prefectureIds}
                      setSelectedIds={(nextPrefectureIds) => {
                        setDraftSearchFilters((previousDraftSearchFilters) => ({
                          ...previousDraftSearchFilters,
                          prefectureIds: nextPrefectureIds,
                        }));
                      }}
                      isOpen={filterModalOpen}
                    />
                  )}
                </ModalSp>
              </>
            ) : (
              <Modal
                open={filterDialogOpen}
                onClose={handleClose}
                aria-labelledby="project-filter-modal-title"
              >
                <Box sx={modalStyle}>
                  <Typography
                    id="project-filter-modal-title"
                    variant="h6"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      padding: 0,
                      margin: "-1px",
                      overflow: "hidden",
                      clip: "rect(0, 0, 0, 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  >
                    検索条件の絞り込み
                  </Typography>
                  <SearchTabs
                    roles={roles}
                    prefByArea={prefByArea}
                    selectedRoleIds={draftSearchFilters.roleIds}
                    setSelectedRoleIds={(nextRoleIds) => {
                      setDraftSearchFilters((previousDraftSearchFilters) => ({
                        ...previousDraftSearchFilters,
                        roleIds: nextRoleIds,
                      }));
                    }}
                    selectedPrefectureIds={draftSearchFilters.prefectureIds}
                    setSelectedPrefectureIds={(nextPrefectureIds) => {
                      setDraftSearchFilters((previousDraftSearchFilters) => ({
                        ...previousDraftSearchFilters,
                        prefectureIds: nextPrefectureIds,
                      }));
                    }}
                    priceFilter={draftSearchFilters.priceFilter}
                    setPriceFilter={setDraftPriceFilter}
                    PriceComponent={Price}
                    type="project"
                    tabLabels={{
                      roles: "ポジション",
                      price: "金額",
                      prefecture: "勤務地",
                    }}
                    onSearch={(filters) => {
                      const nextSearchFilters = {
                        ...searchFilters,
                        roleIds: filters.roleIds,
                        prefectureIds: filters.prefectureIds,
                        priceFilter: filters.priceFilter
                      };

                      setSearchFilters(nextSearchFilters);
                      scrollToResultsTop();
                      executeSearch(nextSearchFilters, {
                        page: 1,
                        per: DEFAULT_PER_PAGE,
                      });
                    }}
                    onClose={handleClose}
                  />
                </Box>
              </Modal>
            )}
          </Box>
        )}
      </Box>

      {/* ===== スクロール領域 ===== */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <Box
          sx={{
            outline: "none",
          }}
        >
          <Outlet
            context={{
              contractExists,
              isActive,
              projects: visibleProjects,
              loading: visibleLoading,
              hasSearchFilters,
              hasExecutedSearch,
              isSearching,
              fetchProjects,
              setProjects,
              handleMarkedRead,
              fetchProjectsBySearch: executeSearch,
              pagination: visiblePagination,
              page,
              goToPage,
            }}
          />
        </Box>
      </Box>

      {/* --- モーダル --- */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title={isActive ? "スキルログ運営局に非公開" : "スキルログ運営局に公開"}
        message={
          isActive
            ? "非公開にすると運営局とのコンタクトができなくなります。"
            : "公開すると運営局からの連絡やプロジェクト紹介を受け取れます。"
        }
        confirmLabel={isActive ? "非公開にする" : "公開する"}
      />
    </Container>
  );
}
